import React from 'react';
import { ToastContainer, cssTransition } from 'react-toastify';
import GlobalStyles from '@mui/material/GlobalStyles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { STATUS_ACCENTS } from '../theme/oceanTokens';
import { TOAST_INTENTS } from '../utils/notify';

/**
 * Toast tones, taken from the status accents so a success here is the same
 * green as a completed appointment and an error the same red as a cancelled
 * one. Nothing new is invented, and a palette change carries through.
 */
const TONES = {
  success: STATUS_ACCENTS.completed,
  error: STATUS_ACCENTS.cancelled,
  warning: STATUS_ACCENTS.booked,
  info: STATUS_ACCENTS.total,
  default: STATUS_ACCENTS.total,
};

const GLYPHS = {
  success: CheckRoundedIcon,
  error: CloseRoundedIcon,
  warning: PriorityHighRoundedIcon,
  info: InfoRoundedIcon,
  default: InfoRoundedIcon,
};

/** Slides in from the right with a small overshoot, and leaves the same way. */
const swoop = cssTransition({
  enter: 'pymed-toast-in',
  exit: 'pymed-toast-out',
  collapseDuration: 260,
});

/** The icon, in a frosted disc so it reads on the gradient. */
const ToastGlyph = ({ type }) => {
  const Glyph = GLYPHS[type] || GLYPHS.default;
  return (
    <span className="pymed-toast-glyph">
      <Glyph sx={{ fontSize: 17 }} />
    </span>
  );
};

/**
 * The tone lives on the ::before layer, NOT on the toast element.
 *
 * This matters for more than tidiness. react-toastify removes a toast when
 * its exit animation raises `animationend`, and `animation` is a single
 * property: a decorative loop on `.Toastify__toast` outranks the one-class
 * `.pymed-toast-out` rule, so the exit animation never runs, the event never
 * fires, and the toast sits on screen for ever. Keeping the drift on a
 * pseudo-element leaves the toast's own `animation` to the transition.
 */
const toneLayer = (selector, tone) => {
  // A tone whose two stops match is a single colour: paint it flat, and skip
  // the drift, which would have nothing to move.
  const flat = tone.from === tone.to;
  return {
    [selector]: {
      boxShadow: `0 6px 16px rgba(8,25,35,0.16), 0 18px 40px ${tone.glow}`,
    },
    [`${selector}::before`]: flat
      ? { background: tone.from, animation: 'none' }
      : {
          background: `linear-gradient(115deg, ${tone.from}, ${tone.to}, ${tone.from})`,
          backgroundSize: '220% 100%',
          animation: 'pymed-toast-drift 9s ease-in-out infinite',
        },
  };
};

/* One pair of rules per toast type, for plain `toast.*` calls. */
const toneRules = Object.entries(TONES).reduce(
  (acc, [type, tone]) => ({
    ...acc,
    ...toneLayer(`.Toastify__toast.Toastify__toast--${type}`, tone),
  }),
  {}
);

/*
 * One pair per intent, for toasts fired through `notify`. The intent class is
 * doubled so it beats the type rule on specificity rather than on source
 * order, which a stylesheet reshuffle could change.
 */
const intentRules = Object.entries(TOAST_INTENTS).reduce(
  (acc, [intent, { tone }]) => ({
    ...acc,
    ...toneLayer(`.Toastify__toast.pymed-intent--${intent}.pymed-intent--${intent}`, tone),
  }),
  {}
);

export const styles = {
  /* ---- entry / exit ---------------------------------------------------- */
  '@keyframes pymed-toast-in': {
    '0%': { opacity: 0, transform: 'translateX(120%) scale(0.92)' },
    '70%': { opacity: 1, transform: 'translateX(-6px) scale(1.01)' },
    '100%': { opacity: 1, transform: 'translateX(0) scale(1)' },
  },
  '@keyframes pymed-toast-out': {
    '0%': { opacity: 1, transform: 'translateX(0) scale(1)' },
    '100%': { opacity: 0, transform: 'translateX(120%) scale(0.94)' },
  },
  '@keyframes pymed-toast-drift': {
    '0%, 100%': { backgroundPosition: '0% 0' },
    '50%': { backgroundPosition: '100% 0' },
  },
  '@keyframes pymed-glyph-in': {
    '0%': { transform: 'scale(0.3) rotate(-30deg)', opacity: 0 },
    '100%': { transform: 'scale(1) rotate(0)', opacity: 1 },
  },

  // Tripled so nothing can outrank the transition — the exit animation is
  // what dismisses the toast, so it has to win the cascade every time.
  '.Toastify__toast.pymed-toast-in.pymed-toast-in': {
    animation: 'pymed-toast-in .42s cubic-bezier(.2,1.25,.35,1) both',
  },
  '.Toastify__toast.pymed-toast-out.pymed-toast-out': {
    animation: 'pymed-toast-out .3s cubic-bezier(.4,0,.8,.2) both',
  },

  /* ---- the surface ----------------------------------------------------- */
  // In v11 this element IS the flex row — it holds the icon, the message text
  // (a bare text node, there is no body wrapper to style), the close button
  // and the progress bar, and it carries role="alert".
  '.Toastify__toast': {
    position: 'relative',
    // Contains the two decorative layers below, which sit at z-index -1.
    isolation: 'isolate',
    background: 'transparent',
    borderRadius: '14px',
    padding: '11px 13px',
    minHeight: 'auto',
    gap: 10,
    alignItems: 'center',
    color: '#fff',
    fontSize: '0.86rem',
    fontWeight: 600,
    lineHeight: 1.45,
    border: '1px solid rgba(255,255,255,0.22)',
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  // The tone. `inset: -1px` puts it under the 1px border, which is
  // semi-transparent white — over the page it would read as a hairline of
  // page colour between the border and the fill.
  '.Toastify__toast::before': {
    content: '""',
    position: 'absolute',
    inset: -1,
    zIndex: -1,
    borderRadius: 'inherit',
    pointerEvents: 'none',
  },
  // A highlight along the top edge, so the gradient reads as a raised surface
  // rather than a flat block of colour.
  '.Toastify__toast::after': {
    content: '""',
    position: 'absolute',
    inset: -1,
    zIndex: -1,
    borderRadius: 'inherit',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.16), transparent 42%)',
    pointerEvents: 'none',
  },
  ...toneRules,
  ...intentRules,

  /* ---- the frosted icon disc ------------------------------------------- */
  '.pymed-toast-glyph': {
    width: 26,
    height: 26,
    flex: 'none',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    color: '#fff',
    background: 'rgba(255,255,255,0.22)',
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.3)',
    animation: 'pymed-glyph-in .4s .06s cubic-bezier(.2,1.5,.4,1) both',
  },
  '.Toastify__toast-icon': { width: 'auto', marginInlineEnd: 0, display: 'flex' },

  /* ---- progress and close ---------------------------------------------- */
  '.Toastify__progress-bar': {
    height: 3,
    background: 'rgba(255,255,255,0.75)',
    boxShadow: '0 0 8px rgba(255,255,255,0.5)',
  },
  '.Toastify__progress-bar--bg': { background: 'rgba(255,255,255,0.2)' },
  '.Toastify__close-button': {
    color: '#fff',
    opacity: 0.7,
    alignSelf: 'center',
    transition: 'opacity .2s, transform .25s',
  },
  '.Toastify__close-button:hover': { opacity: 1, transform: 'rotate(90deg) scale(1.1)' },

  /* Stacked toasts sit a little tighter than the default. */
  '.Toastify__toast-container': { padding: 6 },

  '@media (prefers-reduced-motion: reduce)': {
    // Only the decoration stops. The enter and exit animations are cut to an
    // instant rather than removed, because `animation: none` means no
    // animationend — and no animationend means the toast never goes away.
    '.Toastify__toast::before': { animation: 'none' },
    '.pymed-toast-glyph': { animation: 'none' },
    '.Toastify__toast.pymed-toast-in.pymed-toast-in': { animationDuration: '1ms' },
    '.Toastify__toast.pymed-toast-out.pymed-toast-out': { animationDuration: '1ms' },
    '.Toastify__close-button': { transition: 'none' },
  },
};

/**
 * The app's toasts: one configured container, so the 300-odd `toast.success`
 * and `toast.error` calls scattered through the pages keep working untouched
 * and all pick up the same treatment.
 */
const AppToasts = () => (
  <>
    <GlobalStyles styles={styles} />
    <ToastContainer
      position="top-right"
      autoClose={3200}
      hideProgressBar={false}
      newestOnTop
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      transition={swoop}
      icon={({ type }) => <ToastGlyph type={type} />}
    />
  </>
);

export default AppToasts;
